import React, { useRef, useState } from 'react';
import useSWR from 'swr';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table, Tag } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { useNavigate } from 'react-router-dom';

export interface WarehouseDataType {
    id: number;
    key: React.Key;
    dealer_code: string;
    dealer_name: string;
    warehouse_name: string;
    address: string;
    status: number;
    contact_person: number;
}

type DataIndex = keyof Omit<WarehouseDataType, 'id' | 'key'>;

const fetcher = (url: string) => fetch(url).then((res) => res.json());
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const warehouseApiUrl = backendUrl ? `${backendUrl}/api/warehouse` : null;

export interface InjectedBrowserWarehouseProps {
    columns: TableColumnsType<WarehouseDataType>;
    data: WarehouseDataType[];
    loading: boolean;
    onRow: (record: WarehouseDataType) => React.HTMLAttributes<HTMLElement>;
}

export const withWarehouseData = (WrappedComponent: React.ComponentType<any>) => {
    const ComponentWithWarehouseData: React.FC = (props) => {
        const navigate = useNavigate();
        const { data: rawData, error, isLoading } = useSWR<WarehouseDataType[]>(
            warehouseApiUrl,
            fetcher,
            { shouldRetryOnError: false }
        );
        const [searchText, setSearchText] = useState('');
        const [searchedColumn, setSearchedColumn] = useState('');
        const searchInput = useRef<InputRef>(null);

        const handleSearch = (
            selectedKeys: string[],
            confirm: FilterDropdownProps['confirm'],
            dataIndex: DataIndex,
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText('');
            setSearchedColumn('');
        };

        const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<WarehouseDataType> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Cari ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Cari
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                confirm({ closeDropdown: false });
                                setSearchText((selectedKeys as string[])[0]);
                                setSearchedColumn(dataIndex);
                            }}
                        >
                            Filter
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            Tutup
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                record[dataIndex]
                    ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase())
                    : false,
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    if (visible) {
                        setTimeout(() => searchInput.current?.select(), 100);
                    }
                },
            },
            render: (text) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        });

        const columns: TableColumnsType<WarehouseDataType> = [
             {
                title: 'Dealer Name',
                dataIndex: 'dealer_name',
                key: 'dealer_name',
                width: '25%',
                ...getColumnSearchProps('dealer_name'),
                sorter: (a, b) => a.dealer_name.localeCompare(b.dealer_name),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Dealer Code',
                dataIndex: 'dealer_code',
                key: 'dealer_code',
                width: '15%',
                ...getColumnSearchProps('dealer_code'),
                sorter: (a, b) => a.dealer_code.localeCompare(b.dealer_code),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Warehouse',
                dataIndex: 'warehouse_name',
                key: 'warehouse_name',
                width: '20%',
                ...getColumnSearchProps('warehouse_name'),
                sorter: (a, b) => a.warehouse_name.localeCompare(b.warehouse_name),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Address',
                dataIndex: 'address',
                key: 'address',
                ...getColumnSearchProps('address'),
                sorter: (a, b) => a.address.localeCompare(b.address),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Contact',
                dataIndex: 'contact_person',
                key: 'contact_person',
                width: '15%',
                ...getColumnSearchProps('contact_person'),
                sorter: (a, b) => a.contact_person - b.contact_person,
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: '10%',
                filters: [
                    { text: 'Active', value: 1 },
                    { text: 'Deactive', value: 2 },
                ],
                onFilter: (value, record) => record.status === value,
                render: (status: number) => (
                    <Tag color={status === 1 ? 'green' : 'red'}>
                        {status === 1 ? 'Active' : 'Deactive'}
                    </Tag>
                ),
                sorter: (a, b) => a.status - b.status,
                sortDirections: ['descend', 'ascend'],
            },
        ];

        const processedData = rawData?.map(item => ({
            ...item,
            key: item.id,
            status: Number(item.status) || 2,
        })) || [];

        if (!warehouseApiUrl) {
            return <div style={{ padding: '20px', color: 'red', fontWeight: 'bold' }}>
                       Error: Konfigurasi URL backend (REACT_APP_BACKEND_URL) tidak ditemukan dalam file .env.local atau .env.
                   </div>;
        }

        if (error) return <div>Gagal memuat data... Error: {error.message}</div>;

        const handleRowDoubleClick = (record: WarehouseDataType) => {
            console.log("Navigasi dengan data:", record);
            navigate('/dashboard/warehouse', { state: { warehouseData: record } });
        };

        const onRowProps = (record: WarehouseDataType) => ({
            onDoubleClick: () => handleRowDoubleClick(record),
        });

        return (
            <WrappedComponent
                {...props}
                columns={columns}
                data={processedData}
                loading={isLoading}
                onRow={onRowProps}
            />
        );
    };

    return ComponentWithWarehouseData;
};